import { Radio, UnstyledButton } from '@mantine/core'
import { IconCalendarRepeat, IconRosetteDiscountFilled } from '@tabler/icons-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SellingPlanInterval } from 'src/components/BuyBox/SellingPlanInterval'
import { SubscriptionPerks } from 'src/components/BuyBox/SubscriptionPerks'
import { Text } from 'src/components/Text/Text'
import { PurchaseType } from 'src/constants'
import colorVariables from 'src/styles/Colors.module.scss'

import styles from './PurchaseTypeSwitch.module.scss'

import type { Product } from 'src/data/graphql-generated'
import type { ProductSubscriptionSellingPlans } from 'src/types/Subscription'

interface PurchaseTypeSwitchProps {
  purchaseType: PurchaseType
  setPurchaseType: (purchaseType: PurchaseType) => void
  productType: Product['productType']
  sellingPlanInterval: number
  setSellingPlanInterval: (interval: number) => void
  coffeeSubscriptionSellingPlans: ProductSubscriptionSellingPlans
}

export const PurchaseTypeSwitch = ({
  purchaseType,
  setPurchaseType,
  productType,
  sellingPlanInterval,
  setSellingPlanInterval,
  coffeeSubscriptionSellingPlans,
}: PurchaseTypeSwitchProps) => {
  const { t } = useTranslation()
  const [value, setValue] = useState(purchaseType)

  const changeValue = (v: PurchaseType) => {
    setValue(v)
    setPurchaseType(v)
  }

  const isCoffee = productType === 'coffee'

  return (
    <div className={styles.container}>
      <Text variant="span" bold className={styles.buttonGroupLabel} data-testid="option-group-label">
        {t('pdp.buyBoxModes.purchaseTypeLabel')}
      </Text>

      <div>
        <Radio.Group value={value} onChange={(e) => changeValue(e as PurchaseType)} name="purchase-type">
          <UnstyledButton className={styles.radioCard} onClick={() => changeValue(PurchaseType.oneTime)} w="100%">
            <Radio
              value={PurchaseType.oneTime}
              label={t('pdp.buyBoxModes.oneTime')}
              data-testid="option"
              data-analytics-id="change-purchase-type-one-time"
              classNames={{ radio: styles.radio, label: styles.label }}
            />
          </UnstyledButton>
          <UnstyledButton
            className={styles.subscriptionRadioCard}
            onClick={() => changeValue(PurchaseType.subscription)}
            w="100%"
          >
            <div className={styles.subscriptionRadioCardTopRow}>
              <Radio
                value={PurchaseType.subscription}
                label={isCoffee ? t('pdp.buyBoxModes.subscription') : t('pdp.buyBoxModes.subscriptionGear')}
                data-testid="option"
                data-analytics-id="change-purchase-type-subscription"
                classNames={{ radio: styles.radio, label: styles.label }}
              />
              {isCoffee ? (
                <IconRosetteDiscountFilled color={colorVariables['color-success-primary']} />
              ) : (
                <IconCalendarRepeat color={colorVariables['color-success-primary']} />
              )}
            </div>
            {purchaseType === PurchaseType.subscription && (
              <div className={styles.subscriptionContents}>
                <SubscriptionPerks />
                <SellingPlanInterval
                  sellingPlanInterval={sellingPlanInterval}
                  setSellingPlanInterval={setSellingPlanInterval}
                  coffeeSubscriptionSellingPlans={coffeeSubscriptionSellingPlans}
                />
              </div>
            )}
          </UnstyledButton>
        </Radio.Group>
      </div>
    </div>
  )
}
